import React, { useEffect, useState, useContext } from 'react';
import { useLocation } from "react-router-dom";
import { AuthContext } from "../../../config/AuthContext";
import { Result, Typography, message, Button } from "antd";
import { BookOutlined } from "@ant-design/icons";
import UseWebSocketSubscription from "../../UseWebSocketSubscription";
import { GetUsuariIdJutgeActivitatsId } from "../../../dao/GetUsuariIdJutgeActivitatsId";

export const IdeExamFrame = () => {
    const { userId, user } = useContext(AuthContext);
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const theiaUrl = queryParams.get('url');

    const idPractica = queryParams.get('idPractica') ?? '';
    const idProblema = queryParams.get('idProblema') ?? '';

    const [locked, setLocked] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const [showNotes, setShowNotes] = useState(false);
    const NOTES_URL = "https://daw-0485-prog-5e77eb.gitlab.io/";

    useEffect(() => {
        if (!userId) return;

        // Check initial lock status
        const checkLockStatus = async () => {
            try {
                const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost';
                const token = localStorage.getItem('authToken');
                const response = await fetch(`${baseUrl}:8061/jutge/exam/lock/${userId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (response.ok) {
                    const data = await response.json();
                    setLocked(!!data.locked);
                }
            } catch (err) {
                console.error(err);
            }
        };

        checkLockStatus();
    }, [userId]);

    // ── Check if current exercise is completed ─────────────────────────────────
    useEffect(() => {
        if (!userId || !idPractica || !idProblema) return;
        const checkCompletion = async () => {
            try {
                const data = await GetUsuariIdJutgeActivitatsId(userId + "", idPractica + "");
                if (Array.isArray(data)) {
                    const prob = data.find((p: any) => String(p.id) === String(idProblema));
                    if (prob && prob.progres >= 1) {
                        setIsCompleted(true);
                        // Si ya venía bloqueado pero este ejercicio está completado, desbloquear automáticamente
                        const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost';
                        const token = localStorage.getItem('authToken');
                        await fetch(`${baseUrl}:8061/jutge/exam/unlock`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({ id_usuari: userId })
                        });
                        setLocked(false);
                    }
                }
            } catch (err) {
                console.error("Error verificando si el ejercicio está completado:", err);
            }
        };
        checkCompletion();
    }, [userId, idPractica, idProblema]);

    UseWebSocketSubscription(
        userId ? [`/topic/exam/lock/${userId}`] : [],
        (topic, data) => {
            if (data && data.locked !== undefined) {
                setLocked(data.locked);
            }
        }
    );

    useEffect(() => {
        if (!userId) return;
        if (user.tipus === "0" || user.tipus === "1") return; // Admin avoiding lock
        if (isCompleted) return; // Si el ejercicio está completado, no bloquear al perder foco

        let focusCheckInterval: any = null;
        let consecutiveLosses = 0;

        const executeLock = async () => {
            setLocked(true);
            try {
                const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost';
                const token = localStorage.getItem('authToken');
                await fetch(`${baseUrl}:8061/jutge/exam/lock`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ id_usuari: userId })
                });
            } catch (e) {
                console.error("Error setting lock", e);
            }
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                executeLock();
            }
        };

        const checkFocus = () => {
            if (!document.hasFocus()) {
                consecutiveLosses++;
                // 2 segundos de gracia (2 verificaciones) para evitar falsos positivos
                if (consecutiveLosses >= 2) {
                    executeLock();
                }
            } else {
                consecutiveLosses = 0;
            }
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Alt") {
                e.preventDefault();
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("keydown", handleKeyDown);
        focusCheckInterval = setInterval(checkFocus, 1000);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("keydown", handleKeyDown);
            if (focusCheckInterval) clearInterval(focusCheckInterval);
        };
    }, [userId, user, isCompleted]);

    if (!theiaUrl) {
        return <Typography.Title level={3} style={{ margin: 20 }}>Ninguna URL proporcionada para el entorno.</Typography.Title>
    }

    const isStudentLocked = locked && user.tipus !== "0" && user.tipus !== "1";
    return (
        <div style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
            {isStudentLocked && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
                    backgroundColor: '#fff1f0', display: 'flex', justifyContent: 'center', alignItems: 'center',
                    zIndex: 99999, flexDirection: 'column'
                }}>
                    <Result
                        status="error"
                        title="IDE Bloqueado (Examen)"
                        subTitle="Has perdido el foco de la pestaña (has cambiado de pestaña o minimizado el navegador). El entorno ha sido bloqueado por seguridad. Avisa al profesor administrador para que te lo desbloquee."
                    />
                </div>
            )}
            {/* Toggle Button for Notes */}
            <Button
                type="primary"
                shape="circle"
                icon={<BookOutlined />}
                size="large"
                onClick={() => setShowNotes(!showNotes)}
                style={{
                    position: 'fixed',
                    bottom: 20,
                    right: 20,
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    width: 50,
                    height: 50,
                    backgroundColor: '#1890ff'
                }}
            />

            <div style={{ display: 'flex', flex: 1, width: '100%', height: '100%' }}>
                {/* Main IDE Iframe */}
                <iframe
                    src={theiaUrl}
                    title="IDE Examen"
                    allow="clipboard-read; clipboard-write; display-capture"
                    style={{
                        flex: showNotes ? 1 : 'none',
                        width: showNotes ? '50%' : '100vw',
                        height: '100%',
                        border: 'none',
                        transition: 'width 0.3s ease'
                    }}
                />

                {/* Notes Iframe (Split View) */}
                {showNotes && (
                    <div style={{
                        width: '50%',
                        height: '100%',
                        borderLeft: '2px solid #e8e8e8',
                        backgroundColor: '#fff',
                        position: 'relative'
                    }}>
                        <div style={{
                            padding: '10px 15px',
                            borderBottom: '1px solid #e8e8e8',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: '#fafafa'
                        }}>
                            <Typography.Text strong style={{ color: '#555' }}>
                                <BookOutlined /> Apunts de Clase
                            </Typography.Text>
                            <Button size="small" type="text" onClick={() => setShowNotes(false)}>Cerrar</Button>
                        </div>
                        <iframe
                            src={NOTES_URL}
                            title="Apunts"
                            style={{ width: '100%', height: 'calc(100% - 41px)', border: 'none' }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
